import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { EventInfoResponseBody } from "@services/types/events-management-response.type";
import { defaultEventInfoResponseBody } from "@services/utils/events-management-response.default-values";
import { AddressFormGroup } from "@shared/components/forms/address-form/address-form.form";
import { DateTime } from "luxon";

export type EventDatesGroup = {
  startDateTime: FormControl<Date | null>;
  endDateTime: FormControl<Date | null>;
};

export type MediaGroup = {
  uploadBanner: FormControl<File | null>;
  mapaEvento: FormControl<File | null>;
};

export type BasicInfoGroup = {
  title: FormControl<string>;
  eventCategoryId: FormControl<number | null>;
  description: FormControl<string>;
  media: FormGroup<MediaGroup>;
};

export type EventTypeGroup = {
  isEventPaid: FormControl<boolean>;
};

export type EventSummaryGroup = {
  status: FormControl<string>;
  isPrivate: FormControl<boolean>;
  acceptedTerms: FormControl<boolean>;
};

export type EventFormModel = {
  basicInfo: FormGroup<BasicInfoGroup>;
  eventDates: FormGroup<EventDatesGroup>;
  address: FormGroup<AddressFormGroup>;
  eventType: FormGroup<EventTypeGroup>;
  eventSummary: FormGroup<EventSummaryGroup>;
};

// Value (raw value) do EventFormModel
export type EventFormValue = {
  basicInfo: {
    title: string;
    eventCategoryId: number | null;
    description: string;
    media: {
      uploadBanner: File | null;
      mapaEvento: File | null;
    };
  };

  eventDates: {
    startDateTime: Date | null;
    endDateTime: Date | null;
  };

  address: {
    id: number | null;
    location_name: string;
    country: string;
    postal_code: string;
    city_name: string;
    state: string;
    street: string;
    number: string;
    district: string;
    maps_url: string;
    additional_information: string;
  };

  eventType: {
    isEventPaid: boolean;
  };

  eventSummary: {
    status: string;
    isPrivate: boolean;
    acceptedTerms: boolean;
  };
};

export function buildEventForm(fb: FormBuilder): FormGroup<EventFormModel> {
  return fb.group<EventFormModel>({
    basicInfo: fb.group<BasicInfoGroup>({
      title: fb.nonNullable.control("", Validators.required),
      eventCategoryId: fb.control<number | null>(null, [
        Validators.required,
        Validators.min(1),
      ]),
      description: fb.nonNullable.control("", Validators.required),

      media: fb.group<MediaGroup>({
        uploadBanner: fb.control<File | null>(null, Validators.required),
        mapaEvento: fb.control<File | null>(null),
      }),
    }),

    eventDates: fb.group<EventDatesGroup>({
      startDateTime: fb.nonNullable.control(null, Validators.required),
      endDateTime: fb.nonNullable.control(null, Validators.required),
    }),

    address: fb.group<AddressFormGroup>({
      id: fb.control<null | number>(null),
      location_name: fb.nonNullable.control("", Validators.required),
      country: fb.nonNullable.control("BR", Validators.required),
      postal_code: fb.nonNullable.control("", Validators.required),
      city_name: fb.nonNullable.control("", Validators.required),
      state: fb.nonNullable.control("", Validators.required),
      street: fb.nonNullable.control("", Validators.required),
      number: fb.nonNullable.control("", Validators.required),
      district: fb.nonNullable.control("", Validators.required),
      maps_url: fb.nonNullable.control("", Validators.required),
      additional_information: fb.nonNullable.control(""),
    }),

    eventType: fb.group<EventTypeGroup>({
      isEventPaid: fb.nonNullable.control(true),
    }),

    eventSummary: fb.group<EventSummaryGroup>({
      status: fb.nonNullable.control("", Validators.required),
      isPrivate: fb.nonNullable.control(false, Validators.required),
      acceptedTerms: fb.nonNullable.control(false, Validators.requiredTrue),
    }),
  });
}

export function generateFormDefaulValue() {
  const now = new Date();

  const oneDayAfter = new Date(now);
  oneDayAfter.setDate(now.getDate() + 1);

  const sevenDaysAfter = new Date(now);
  sevenDaysAfter.setDate(now.getDate() + 7);
  const defaultEventFormValue: Partial<EventFormValue> = {
    basicInfo: {
      title: "",
      eventCategoryId: null,
      description: "",
      media: {
        uploadBanner: null,
        mapaEvento: null,
      },
    },

    eventDates: {
      startDateTime: oneDayAfter,
      endDateTime: sevenDaysAfter,
    },

    address: {
      location_name: "",
      country: "BR",
      postal_code: "",
      city_name: "",
      state: "",
      street: "",
      number: "",
      district: "",
      maps_url: "",
      additional_information: "",
      id: null,
    },

    eventType: {
      isEventPaid: true,
    },

    eventSummary: {
      status: "published",
      isPrivate: false,
      acceptedTerms: false,
    },
  };

  return defaultEventFormValue;
}

// !IMPORTANT: Talvez tenha que receber o ticketBatches tbm pra poder popular isso corretamente
export function mapEventInfoToEventFormValue(
  event: EventInfoResponseBody,
): EventFormValue {
  // tenta montar um Date válido (vêm como ISO normalmente)
  const start = DateTime.fromISO(event.start_date);
  const end = DateTime.fromISO(event.end_date);

  return {
    basicInfo: {
      title: event.title ?? "",
      eventCategoryId: event.event_category?.id > 0 ? event.event_category.id : null,
      description: event.description ?? "",
      // arquivos não vêm do backend (só URL), então ficam null
      media: {
        uploadBanner: null,
        mapaEvento: null,
      },
    },

    eventDates: {
      startDateTime: start.isValid ? start.toJSDate() : null,
      endDateTime: end.isValid ? end.toJSDate() : null,
    },

    address: {
      id: event.address?.id ?? null,
      location_name: event.address?.location_name ?? "",
      country: event.address?.country ?? "BR",
      postal_code: event.address?.postal_code ?? "",
      city_name: event.address?.city_name ?? "",
      state: event.address?.state ?? "",
      street: event.address?.street ?? "",
      number: event.address?.number ?? "",
      district: event.address?.district ?? "",
      maps_url: event.address?.maps_url ?? "",
      additional_information: event.address.additional_information ?? "",
    },

    eventType: {
      isEventPaid: event.event_type !== "free",
    },

    eventSummary: {
      status: event.status ?? "",
      isPrivate: !!event.is_private,
      acceptedTerms: !!event.accepted_terms,
    },
  };
}

export function mapEventFormValueToEventInfo(
  form: EventFormValue,
  base?: EventInfoResponseBody,
): EventInfoResponseBody {
  const current = base ?? defaultEventInfoResponseBody;

  const startISO = form.eventDates.startDateTime
    ? DateTime.fromJSDate(form.eventDates.startDateTime).toISO()
    : current.start_date;

  const endISO = form.eventDates.endDateTime
    ? DateTime.fromJSDate(form.eventDates.endDateTime).toISO()
    : current.end_date;

  return {
    ...current,

    title: form.basicInfo.title,
    description: form.basicInfo.description,

    status: form.eventSummary.status,
    is_private: form.eventSummary.isPrivate,
    accepted_terms: form.eventSummary.acceptedTerms,

    start_date: startISO ?? current.start_date,
    end_date: endISO ?? current.end_date,

    event_category: {
      // mantém o title do base (ou vazio no create)
      ...current.event_category,
      id: form.basicInfo.eventCategoryId ?? 0,
    },

    address: {
      ...current.address,
      location_name: form.address.location_name,
      country: form.address.country,
      city_name: form.address.city_name,
      state: form.address.state,
      district: form.address.district?.trim() ? form.address.district : null,
      street: form.address.street,
      postal_code: form.address.postal_code,
      number: form.address.number,
      maps_url: form.address.maps_url,
    },
  };
}
